import type { ReactNode } from 'react';
import { Lockup } from '../brand/Lockup';
import { INFO_EMAIL } from './BetaNotice';
import { Footer, Gutter, Page } from './Chrome';

/**
 * The chrome the two published documents share — privacy and terms (TROOP-203).
 *
 * **One component rather than two copies**, for the reason `BetaNotice` is one component: these
 * pages say the same thing in the same shape twice, and the version that writes the shape out
 * each time is the one where the version stamp is rendered on one of them and not the other. It
 * is `AboutScreen`'s layout deliberately — the same lockup, the same measure, the same footer —
 * because all three are the pages somebody evaluating the product reads in one sitting, and a
 * legal document that looks like a different website is one a committee chair stops trusting.
 *
 * **No masthead**, following `AboutScreen` and `SignInScreen`: the lockup sits at the top of the
 * content and is the way back to the front page. A second inline masthead would be a third one to
 * keep in step.
 *
 * **The prose elements are here rather than inline in each document** because a paragraph in a
 * legal page is `t-body` at a 54ch measure and nothing else, forty times over, and forty copies of
 * an inline style object is forty chances for one of them to drift. They are deliberately dumb:
 * no variants, no props beyond children. Anything a document needs that these do not do is a sign
 * the document is doing something the design system has not been asked about.
 */
export function LegalDocument({
  title,
  version,
  children,
}: {
  title: string;
  /** The published version, quoted verbatim — see `PRIVACY_POLICY_VERSION`. */
  version: string;
  children: ReactNode;
}) {
  return (
    <Page>
      <Gutter>
        <div style={{ paddingBlock: 'var(--space-7)', maxWidth: '46rem' }}>
          {/* `Lockup` pulls its own optical inset back out, so no negative margin here — see
              AboutScreen, where a second correction copied from `AppBar` put the mark 8px left of
              every paragraph beneath it. */}
          <a href="/" aria-label="TroopHerald home" style={{ display: 'inline-block' }}>
            <Lockup size={30} />
          </a>

          <h1
            className="t-display"
            style={{
              color: 'var(--color-text-primary)',
              margin: 'var(--space-6) 0 var(--space-3)',
              textWrap: 'balance',
            }}
          >
            {title}
          </h1>

          {/* The version is a fact value, so it is tabular and it is quotable. It sits under the
              title rather than at the foot of the page because the reader who needs it — a parent
              asking which document they were shown — should not have to scroll a legal page to
              find out they are looking at the wrong one. */}
          <p
            className="t-caption tabular"
            style={{ color: 'var(--color-text-secondary)', margin: 0 }}
          >
            Version {version}
          </p>

          {children}
        </div>
      </Gutter>

      <Footer />
    </Page>
  );
}

/** A numbered-in-spirit part of a document: a heading and the prose under it. */
export function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginTop: 'var(--space-7)' }}>
      <h2
        className="t-heading"
        style={{ color: 'var(--color-text-primary)', margin: 0, textWrap: 'balance' }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

/** A subheading inside a section, for the parts a reader skims to find. */
export function LegalSubheading({ children }: { children: ReactNode }) {
  return (
    <h3
      className="t-subheading"
      style={{
        color: 'var(--color-text-primary)',
        margin: 'var(--space-6) 0 0',
        textWrap: 'balance',
      }}
    >
      {children}
    </h3>
  );
}

/** A paragraph of the document. 54ch is the product's body measure, as on About. */
export function LegalText({ children }: { children: ReactNode }) {
  return (
    <p
      className="t-body"
      style={{
        color: 'var(--color-text-secondary)',
        margin: 'var(--space-3) 0 0',
        maxWidth: '54ch',
      }}
    >
      {children}
    </p>
  );
}

/**
 * A bulleted list.
 *
 * SPEC-GAP: `handoff/design-system.md` has no treatment for prose lists — every list in the
 * product until now is a list of records with `listStyle: 'none'`, and Tailwind's preflight
 * strips markers. A legal document is the one place a reader genuinely wants them, so the marker
 * comes back and the indent is `--space-5`, an existing step rather than a derived one.
 */
export function LegalList({ children }: { children: ReactNode }) {
  return (
    <ul
      className="flex flex-col"
      style={{
        gap: 'var(--space-2)',
        listStyle: 'disc',
        margin: 'var(--space-3) 0 0',
        paddingInlineStart: 'var(--space-5)',
        maxWidth: '54ch',
      }}
    >
      {children}
    </ul>
  );
}

export function LegalItem({ children }: { children: ReactNode }) {
  return (
    <li className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
      {children}
    </li>
  );
}

/** Emphasis inside a paragraph, at the design system's own strong step rather than `bolder`. */
export function LegalStrong({ children }: { children: ReactNode }) {
  return (
    <strong className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
      {children}
    </strong>
  );
}

/**
 * The contact address. One definition of it (`BetaNotice`'s `INFO_EMAIL`, for the reason that
 * component gives — an address that has drifted on one surface is somebody writing into a void with
 * nothing on either side to say so) and one definition of this rendering, because the two documents
 * publish it seven times between them.
 *
 * <b>`BetaNotice` renders it differently on purpose, and this is not a copy of it.</b> There it is
 * a 44px button plus a separate `userSelect: 'all'` span, because a `mailto:` opens nothing on a
 * device with no mail client configured and a button hides the address inside itself. Here the
 * address <i>is</i> the link text, so it can be read and copied whether or not the `mailto:` does
 * anything — the same requirement met the only way a sentence allows. A button in the middle of a
 * paragraph is not available to us.
 *
 * SPEC-GAP: not a 44px tap target, unlike everything else in the product. §10 says "without
 * exception" and this is the exception the criterion it comes from actually makes — WCAG 2.2's
 * target-size rule exempts a target inline in a block of text, because giving one height pushes the
 * sentence apart. The footer's links are not inline and still get the height.
 */
export function LegalAddress() {
  return (
    <a href={`mailto:${INFO_EMAIL}`} style={{ ...INLINE_LINK, overflowWrap: 'anywhere' }}>
      {INFO_EMAIL}
    </a>
  );
}

/**
 * A link from inside a sentence — these two documents point at each other constantly.
 *
 * <b>Underlined, because Tailwind's preflight sets `a { color: inherit; text-decoration: inherit }`
 * and nothing in the product puts it back.</b> Everywhere else that is fine: a link in this app is
 * a row, a card or a nav label, and its position says what it is. In a paragraph there is no
 * position to read, so an undecorated link is a control nobody can see — the footer's own failure
 * inverted. The product's existing textual links (`FamilyScreen`, `PhotosScreen`) underline and
 * keep the text colour rather than introducing a link colour, and this follows them: there is no
 * blue in the palette and §2 forbids inventing one.
 */
const INLINE_LINK = { textDecoration: 'underline' } as const;

export function LegalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} style={INLINE_LINK}>
      {children}
    </a>
  );
}
